import React from 'react';
import camelize from 'camelize';

//Hooks
import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

//Actions
import { getReservationDetails, cancelBooking } from '../../../store/actions';

//Components
import { Container, Row, Modal, Button } from 'react-bootstrap';
import GoogleMapReact from 'google-map-react';
import Loader from 'react-loader-spinner';
import Col from '../../../components/Col/Col';
import Icon from '../../../components/Icon/Icon';
import SubmitButton from '../../../components/ui/SubmitButton/SubmitButton';

//Styles
import Styles from './Details.module.scss';

//Utils
import moment from 'moment';
import googleMapInput from '../../../services/settings';
import { PAYMENT_TYPE } from '../../../const/payment/PAYMENT_TYPE';
import { PAYMENT_SYSTEM_TYPE } from '../../../const/payment/PAYMENT_SYSTEM_TYPE';
import { formatPrice } from '../../../helpers/function/format';

const Marker = () => (
    <div className={Styles.markerContainer}>
        <div className={Styles.marker}/>
    </div>
);

const NOT_IDEAL_PAYMENTS = [PAYMENT_TYPE.CASH, PAYMENT_TYPE.SUBSCRIPTION, PAYMENT_TYPE.BUNDLE, PAYMENT_TYPE.DISCOUNT];

const Details = () => {
    const { reservationId, reservationType, next } = useParams();

    const tintColor = useSelector(state => state.companies.tintColor);
    const reservation = useSelector(state => state.reservations.reservationDetails);
    const selectedCompany = useSelector(state => state.companies.selectedCompany)
    const dispatch = useDispatch();
    const { t } = useTranslation('common');

    const [isLoading, setIsLoading] = useState(false);
    const [show, setShow] = useState(false);

    const company = camelize(selectedCompany.company)

    const {
        final_cost: finalCost,
        max_usages: maxUsages,
        usages,
        ends_at: endsAt,
        length,
        length_type: lengthType,
        services,
        description,
        payment_info: paymentInfo,
        payment_system: paymentSystem,
        paid,
        address,
        service,
        timestamp,
        minutes,
        solarium_name: productName,
        photo_url: photoURL,
        cancellation_policy: cancellationPolicy,
        is_bought_with_bundle: isBoughtWithBundle,
        is_bought_with_subscription: isBoughtWithSubscription,
    } = reservation ?? {};

    useEffect(() => {
        dispatch(getReservationDetails({
            id: reservationId,
            type: reservationType,
            next,
        }))
    }, [reservationId, reservationType, next, dispatch]);

    const handleCancel = (all) => {
        setShow(false);
        dispatch(cancelBooking(parseInt(reservationId, 10), all));
        setIsLoading(true);
    };

    const isPayProPayment = paymentSystem === PAYMENT_SYSTEM_TYPE.PAY_PRO
        && paymentInfo?.toLowerCase() === PAYMENT_TYPE.IDEAL;

    const confirmCancelText = (() => {
        const isFreeBooking = !finalCost || !paid;
        const isBundleOrSubscriptionPaymentType = isBoughtWithBundle || isBoughtWithSubscription;
        const hasCancellationFee = !NOT_IDEAL_PAYMENTS.includes(paymentInfo?.toLowerCase());

        if (moment.unix(timestamp).diff(moment(), 'hours') > (cancellationPolicy ?? 0)) {
            if (isFreeBooking || !hasCancellationFee) {
                return isBundleOrSubscriptionPaymentType ? t('common:reservationScreen.cancelConfirmation.freeBookingReturnUsage'): t('common:reservationScreen.cancelConfirmation.freeBooking');
            }

            return hasCancellationFee ? (
                t(
                    'common:reservationScreen.cancelConfirmation.returnCost',
                    { price: company.cancellationFee ?? 0 }
                )
            ) : t('common:reservationScreen.cancelConfirmation.returnUsage');
        }

        if (isBundleOrSubscriptionPaymentType) {
            return t('common:reservationScreen.cancelConfirmation.notReturnUsage');
        }

        if (hasCancellationFee) {
            return t('common:reservationScreen.cancelConfirmation.notReturnCost');
        }

        return t('common:reservationScreen.cancelConfirmation.canNotCanceled');
    })()

    const modalWithTitleAndButton = !isPayProPayment
        && confirmCancelText  !== t('common:reservationScreen.cancelConfirmation.canNotCanceled');

    if (!reservation) {
        return (
            <div className={Styles.container}>
                <Loader type="TailSpin" color="#000" height={40} width={40}/>
            </div>
        )
    }

    return (
        <>
            <div className={Styles.container}>
                <Modal show={show} container={document.getElementById('root_appybee_widget')}
                       onHide={() => setShow(false)} centered>
                    <Modal.Header closeButton>
                        <Modal.Title style={{ fontWeight: 'bold' }}>
                            <span data-testid="data-test-modal-title">
                                {modalWithTitleAndButton && t('common:reservationScreen.confirmCancellation')}
                        </span>
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <span data-testid="data-test-modal-body">
                            {isPayProPayment
                                ? t("common:reservationScreen.payProWarning")
                                : confirmCancelText
                            }
                        </span>
                    </Modal.Body>
                    {modalWithTitleAndButton && <Modal.Footer>
                        <Button data-testid="cancel-btn" variant="outline-dark" onClick={() => setShow(false)}>
                            {t('common:reservationScreen.noLeave')}
                        </Button>
                        <Button data-testid="confirm-btn" variant="danger" onClick={handleCancel}>
                            {t('common:reservationScreen.yesCancel')}
                        </Button>
                    </Modal.Footer>}
                </Modal>
                <Container className={'px-0'}>
                    <Row className={Styles.columnContainer}>
                        <Col className={Styles.column} xs={12} md={6}>
                            <span 
                                className={Styles.finalCost}
                                data-testid="data-test-final-cost"
                            >
                                {formatPrice(finalCost)}
                            </span>
                            {
                                reservationType === 'product' ?
                                    <>
                                        <div className={Styles.detailsContainer}>
                                            <div className={Styles.details}>
                                                <Icon
                                                    size={18}
                                                    name="calendar"
                                                    className={'mr-3'}
                                                    color={tintColor}
                                                />
                                                <span style={{ flex: 0.4 }}>
                                                {t('reservationScreen.date')}:
                                            </span>
                                                <span style={{ flex: 0.6 }}>
                                                <strong
                                                    data-testid="data-test-date"
                                                >
                                                    {moment.unix(timestamp).utc().format('ddd D MMMM YYYY')}
                                                </strong>
                                            </span>
                                            </div>
                                            <div className={Styles.details}>
                                                <Icon
                                                    size={18}
                                                    name="clock"
                                                    className={'mr-3'}
                                                    color={tintColor}
                                                />
                                                <span style={{ flex: 0.4 }}>
                                                {t('reservationScreen.duration')}:
                                            </span>
                                                <span style={{ flex: 0.6 }}>
                                                <strong
                                                    data-testid="data-test-duration"
                                                >
                                                    {`${moment.unix(timestamp).utc().format('HH:mm')} - ${moment.unix(timestamp).utc().add(minutes, 'minutes').format('HH:mm')}`}
                                                </strong>
                                            </span>
                                            </div>
                                        </div>
                                        <div className={Styles.servicesContainer}>
                                        <span className={Styles.subTitle}>
                                            {t('reservationScreen.bookedServices')}
                                        </span>
                                            <span 
                                                data-testid="data-test-service"
                                                className={Styles.wrapSpan}>{service}</span>
                                        </div>
                                        {
                                            next === '1' && (
                                                <div className={Styles.cancelButton}>
                                                    <SubmitButton
                                                        data-testid="data-test-cancel-booking"
                                                        isLoading={isLoading}
                                                        propsClass={Styles.button}
                                                        submitHandler={() => setShow(true)}
                                                    >
                                                        {t('reservationScreen.cancelBooking')}
                                                    </SubmitButton>
                                                </div>
                                            )
                                        }
                                        <div className={Styles.imgContainer}>
                                            <img className={Styles.img} src={photoURL} alt={service}/>
                                            <span className={Styles.imgSpan}>
                                            <strong
                                                data-testid="data-test-product-name"
                                            >{productName}</strong>
                                        </span>
                                        </div>
                                    </>
                                    :
                                    <>
                                        <div className={Styles.detailsContainer}>
                                            <div className={Styles.details}>
                                                <Icon
                                                    size={18}
                                                    name="box"
                                                    className={'mr-3'}
                                                    color={tintColor}
                                                />
                                                <span style={{ flex: 0.4 }}>
                                                {t('reservationScreen.usedTotal')}:
                                            </span>
                                                <span style={{ flex: 0.6 }}>
                                                <strong
                                                    data-testid="data-test-usage"
                                                >{usages}/{maxUsages ?? '∞'}</strong>
                                            </span>
                                            </div>
                                            <div className={Styles.details}>
                                                <Icon
                                                    size={18}
                                                    name="clock"
                                                    className={'mr-3'}
                                                    color={tintColor}
                                                />
                                                <span style={{ flex: 0.4 }}>
                                                {t('reservationScreen.expiresIn')}:
                                            </span>
                                                <span style={{ flex: 0.6 }}>
                                                <strong>
                                                    {lengthType && lengthType !== null ? t(`reservationScreen.${lengthType.split('.')[2]}`, { count: length }) : length}
                                                </strong>
                                            </span>
                                            </div>
                                            {
                                                length !== '∞' ?
                                                    <div className={Styles.details}>
                                                        <Icon
                                                            size={18}
                                                            name="calendar"
                                                            className={'mr-3'}
                                                            color={tintColor}
                                                        />
                                                        <span style={{ flex: 0.4 }}>
                                                    {t('reservationScreen.expiresAt')}:
                                                </span>
                                                        <span style={{ flex: 0.6 }}>
                                                    <strong data-testid="data-test-expires-at">
                                                        {moment.unix(endsAt).utc().format('ddd D MMMM YYYY')}
                                                    </strong>
                                                </span>
                                                    </div> :
                                                    null
                                            }
                                        </div>

                                        <div className={Styles.servicesContainer}>
                                            <span className={Styles.subTitle}>
                                                {t('reservationScreen.includedServices')}
                                            </span>
                                            {services && services.length !== 0 && services.map((val, index) => (
                                                <span className={'d-block mb-1'} key={index} data-testid={`data-test-service-${val.name}`}>
                                                    {val.name}
                                                </span>
                                            ))}
                                        </div>
                                    </>
                            }
                        </Col>
                        <Col className={Styles.column} xs={12} md={6}>
                            <div className={Styles.paymentContainer}>
                                <span className={Styles.subTitle}>
                                    {t('reservationScreen.paymentMethod')}
                                </span>

                                <span
                                    data-testid="data-test-payment-info"
                                >{paymentInfo}</span>

                                <span
                                    style={{ fontWeight: 'bold', color: paid ? '#00C102' : '#FF0000' }}
                                    className={'ml-1'}
                                    data-testid="data-test-payment-status"
                                >
                                    {paid ? 'PAID' : 'UNPAID'}
                                </span>
                            </div>

                            {reservationType === 'product' && (
                                <>
                                    <div className={Styles.map}>
                                        <GoogleMapReact
                                            bootstrapURLKeys={{ key: googleMapInput }}
                                            defaultCenter={{
                                                lat: address?.latitude,
                                                lng: address?.longitude
                                            }}
                                            defaultZoom={11}
                                        >
                                            <Marker lat={address?.latitude} lng={address?.longitude}/>
                                        </GoogleMapReact>
                                    </div>

                                    <div className={Styles.shopContainer}>
                                        <div className={Styles.contactContainer}>
                                            <Icon
                                                size={18}
                                                name="map-pin"
                                                className={'mr-3'}
                                                color="#B3BDC8"
                                            />

                                            <div>
                                                <span
                                                    className={'d-block font-weight-bold'}
                                                    style={{ fontSize: 16 }}
                                                    data-testid="data-test-shop-name"
                                                >
                                                    {address?.street}
                                                </span>
                                                <span
                                                    className={'d-block'}
                                                    style={{ fontSize: 16 }}
                                                    data-testid="data-test-shop-address"
                                                >
                                                    {`${address?.zip} ${address?.city}`}
                                                </span>
                                                <span
                                                    className={'d-block'}
                                                    style={{ fontSize: 16 }}
                                                    data-testid="data-test-shop-country"
                                                >
                                                    {`${address?.country}`}
                                                </span>
                                            </div>
                                        </div>
                                        {address?.contact_number && (
                                            <div className={Styles.contactContainer}>
                                                <Icon
                                                    size={18}
                                                    name="phone"
                                                    className={'mr-3'}
                                                    color="#B3BDC8"
                                                />
                                                <div>
                                                    <span
                                                        className={'d-block font-weight-bold'}
                                                        style={{ fontSize: 17 }}
                                                        data-testid="data-test-shop-phone"
                                                    >
                                                        {address?.contact_number}
                                                    </span>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}

                            {reservationType === 'bundle' && (
                                <div className={Styles.servicesContainer}>
                                    <span className={Styles.subTitle}>
                                        {t('reservationScreen.info')}
                                    </span>
                                    <span
                                        data-testid="data-test-description"
                                    >{description}</span>
                                </div>
                            )}
                        </Col>
                    </Row>
                </Container>
            </div>
        </>
    )
}

export default Details
